/////////////////////////////////////////////////////////
// Configurations related to listing.                  //
// Main configuration here is the extended data config //
/////////////////////////////////////////////////////////

// Note: The listingFields come from listingFields asset nowadays by default.
//       To use this built-in configuration, you need to change the overwrite from configHelper.js
//       (E.g. use mergeDefaultTypesAndFieldsForDebugging func)

import { TOL_LEVEL_5_CATEGORIES, TOL_LEVEL_2_CATEGORIES, getGroupedTOLLevel2Categories } from './tolCategories';

/**
 * Configuration options for listing fields (custom extended data fields):
 * - key:                           Unique key for the extended data field.
 * - scope (optional):              Scope of the extended data can be either 'public' or 'private'.
 *                                  Default value: 'public'.
 *                                  Note: listing doesn't support 'protected' scope atm.
 * - schemaType (optional):         Schema for this extended data field.
 *                                  This is relevant when rendering components and querying listings.
 *                                  Possible values: 'enum', 'multi-enum', 'text', 'long', 'boolean'.
 * - enumOptions (optional):        Options shown for 'enum' and 'multi-enum' extended data.
 *                                  These are used to render options for inputs and filters on
 *                                  EditListingPage, ListingPage, and SearchPage.
 * - listingTypeConfig (optional):  Relationship configuration against listing types.
 *   - limitToListingTypeIds:         Indicator whether this listing field is relevant to a limited set of listing types.
 *   - listingTypeIds:                An array of listing types, for which this custom listing field is
 *                                    relevant and should be added. This is mandatory if limitToListingTypeIds is true.
 * - categoryConfig (optional):     Relationship configuration against categories.
 *   - limitToCategoryIds:            Indicator whether this listing field is relevant to a limited set of categories.
 *   - categoryIds:                   An array of categories, for which this custom listing field is
 *                                    relevant and should be added. This is mandatory if limitToCategoryIds is true.
 * - filterConfig:                  Filter configuration for listings query.
 *    - indexForSearch (optional):    If set as true, it is assumed that the extended data key has
 *                                    search index in place. I.e. the key can be used to filter
 *                                    listing queries (then scope needs to be 'public').
 *                                    Note: Flex CLI can be used to set search index for the key:
 *                                    https://www.sharetribe.com/docs/references/extended-data/#search-schema
 *                                    Read more about filtering listings with public data keys from API Reference:
 *                                    https://www.sharetribe.com/api-reference/marketplace.html#extended-data-filtering
 *                                    Default value: false,
 *   - filterType:                    Sometimes a single schemaType can be rendered with different filter components.
 *                                    For 'enum' schema, filterType can be 'SelectSingleFilter' or 'SelectMultipleFilter'
 *   - label:                         Label for the filter, if the field can be used as query filter
 *   - searchMode (optional):         Search mode for indexed data with multi-enum schema.
 *                                    Possible values: 'has_all' or 'has_any'.
 *   - group:                         SearchPageWithMap has grouped filters. Possible values: 'primary' or 'secondary'.
 * - showConfig:                    Configuration for rendering listing. (How the field should be shown.)
 *   - label:                         Label for the saved data.
 *   - isDetail                       Can be used to hide detail row (of type enum, boolean, or long) from listing page.
 *                                    Default value: true,
 * - saveConfig:                    Configuration for adding and modifying extended data fields.
 *   - label:                         Label for the input field.
 *   - placeholderMessage (optional): Default message for user input.
 *   - isRequired (optional):         Is the field required for providers to fill
 *   - requiredMessage (optional):    Message for those fields, which are mandatory.
 */
export const listingFields = [
  // TOL 2008 Industry Category Filter (Level 2 - Main Category)
  // Searchable filter for main industry categories
  // 88 main categories - searchable by typing
  // NOTE: For company listings and service requests (so requests reach the right businesses)
  {
    key: 'tolLevel2',  // 2-digit TOL industry code - matches flex-cli search schema
    scope: 'public',
    schemaType: 'enum',
    // All 88 Level 2 main categories from TOL 2008 standard, grouped by Level 1 sections (A-U)
    // Searchable filter allows users to type and find their industry
    // Groups shown as headers (e.g., "F Rakentaminen") with indented categories below
    enumOptions: getGroupedTOLLevel2Categories().map(cat => ({
      option: cat.code,
      label: cat.label,
      isDisabled: cat.isHeader, // Section headers can't be selected
    })),
    listingTypeConfig: {
      limitToListingTypeIds: true,
      listingTypeIds: ['company-profile', 'ilmoitus', 'request'],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SearchableSelectFilter',
      label: 'Toimiala / Industry',
      group: 'primary',
    },
    showConfig: {
      label: 'Toimiala',
      isDetail: true,
    },
  },

  // TOL 2008 Industry Subcategory Filter (Level 5 - Detailed Subcategory)
  // Searchable autocomplete for precise industry filtering
  // 576 subcategories - searchable by typing
  // NOTE: This is a DEPENDENT filter - only shows subcategories for selected tolLevel2
  // NOTE: Only for company listings, not for request listings
  {
    key: 'tolLevel5',  // 5-digit TOL industry code - matches flex-cli search schema
    scope: 'public',
    schemaType: 'enum',
    // All 576 Level 5 subcategories from Excel data (excluding unknown 00000)
    // Searchable filter allows users to type and find their specific industry
    // Now displays with 5-digit TOL code for easy identification (e.g., "41201 Asuin- ja muiden rakennusten rakentaminen")
    enumOptions: TOL_LEVEL_5_CATEGORIES.map(cat => ({
      option: cat.code,
      label: `${cat.code} ${cat.label}`,
    })),
    listingTypeConfig: {
      limitToListingTypeIds: true,
      listingTypeIds: ['company-profile', 'ilmoitus'],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SearchableSelectFilter',
      label: 'Alakategoria / Subcategory',
      group: 'primary',
      searchMode: 'has_all',
      // Dependency: This field only shows subcategories for selected tolLevel2
      dependsOn: {
        field: 'tolLevel2',
        queryParam: 'pub_tolLevel2',
        filterFunction: 'filterTOLLevel5ByLevel2',
      },
    },
    showConfig: {
      label: 'Alakategoria',
      isDetail: true,
    },
  },

  // Sub-Region Filter (Geographic Filtering)
  // Searchable filter for Finnish sub-regions (seutukunta)
  // 7 sub-regions in Pohjois-Pohjanmaa region
  // NOTE: This replaces the non-functional 'region' (maakunta) filter
  {
    key: 'subRegion',  // Matches flex-cli search schema
    scope: 'public',
    schemaType: 'enum',
    // All 7 sub-regions found in the data, sorted by listing count
    enumOptions: [
      { option: 'Oulu', label: 'Oulu' },
      { option: 'Ylivieska', label: 'Ylivieska' },
      { option: 'Nivala-Haapajärvi', label: 'Nivala-Haapajärvi' },
      { option: 'Oulunkaari', label: 'Oulunkaari' },
      { option: 'Raahe', label: 'Raahe' },
      { option: 'Koillismaa', label: 'Koillismaa' },
      { option: 'Haapavesi-Siikalatva', label: 'Haapavesi-Siikalatva' },
    ],
    filterConfig: {
      indexForSearch: true,
      filterType: 'SearchableSelectFilter',
      label: 'Alue / Area',
      group: 'primary',
    },
    showConfig: {
      label: 'Alue',
      isDetail: true,
    },
  },

  // TASK053: Event-specific filters (only shown when Events category selected)
  // Event Type Filter
  {
    key: 'eventType',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'conference', label: 'Konferenssi / Conference' },
      { option: 'workshop', label: 'Työpaja / Workshop' },
      { option: 'seminar', label: 'Seminaari / Seminar' },
      { option: 'webinar', label: 'Webinaari / Webinar' },
      { option: 'networking', label: 'Verkostoituminen / Networking' },
      { option: 'training', label: 'Koulutus / Training' },
      { option: 'exhibition', label: 'Näyttely / Exhibition' },
      { option: 'meetup', label: 'Tapaaminen / Meetup' },
      { option: 'other', label: 'Muu / Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: ['tapahtumat', 'events'],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Tapahtuman tyyppi / Event Type',
      group: 'primary',
      searchMode: 'has_any',
    },
    showConfig: {
      label: 'Tapahtuman tyyppi',
      isDetail: true,
    },
  },

  // Event Date/Time Field (for filtering events by timing)
  // NOTE: Uses the existing 'ajankohta' field mentioned in TASK047 context
  {
    key: 'ajankohta',
    scope: 'public',
    schemaType: 'text',
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: ['tapahtumat', 'events'],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'KeywordFilter',
      label: 'Ajankohta / Date',
      group: 'primary',
    },
    showConfig: {
      label: 'Ajankohta',
      isDetail: true,
    },
    saveConfig: {
      label: 'Ajankohta / Event Date',
      placeholderMessage: 'Esim. "15.3.2026" tai "Maaliskuu 2026"',
    },
  },

  // Event Tags (for keyword-based event filtering)
  {
    key: 'eventTags',
    scope: 'public',
    schemaType: 'multi-enum',
    enumOptions: [
      { option: 'business', label: 'Liiketoiminta / Business' },
      { option: 'technology', label: 'Teknologia / Technology' },
      { option: 'marketing', label: 'Markkinointi / Marketing' },
      { option: 'sales', label: 'Myynti / Sales' },
      { option: 'hr', label: 'Henkilöstö / HR' },
      { option: 'finance', label: 'Rahoitus / Finance' },
      { option: 'leadership', label: 'Johtaminen / Leadership' },
      { option: 'innovation', label: 'Innovaatio / Innovation' },
      { option: 'sustainability', label: 'Kestävyys / Sustainability' },
      { option: 'healthcare', label: 'Terveys / Healthcare' },
      { option: 'education', label: 'Koulutus / Education' },
      { option: 'culture', label: 'Kulttuuri / Culture' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: ['tapahtumat', 'events'],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Aiheet / Topics',
      group: 'secondary',
      searchMode: 'has_any',
    },
    showConfig: {
      label: 'Aiheet',
      isDetail: true,
    },
  },

  // TASK056 Phase 1: Announcement Type Field (only for announcement/event categories)
  // Determines delivery mechanism for announcements and events
  {
    key: 'announcementType',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'ilmoitus', label: 'Ilmoitus / Standard Announcement' },
      { option: 'tapahtuma', label: 'Tapahtuma / Event' },
      { option: 'varaus', label: 'Varaus / Daily Booking' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: ['tapahtumat', 'events'],
    },
    filterConfig: {
      indexForSearch: false,
      filterType: 'SelectSingleFilter',
      label: 'Announcement Type',
      group: 'primary',
    },
    showConfig: {
      label: 'Ilmoitustyyppi',
      isDetail: true,
    },
    saveConfig: {
      label: 'Ilmoitustyyppi / Announcement Type',
      placeholderMessage: 'Valitse ilmoitustyyppи',
      isRequired: false,
      requiredMessage: 'You need to select an announcement type.',
    },
  },

  // {
  //   "scope": "public",
  //   "label": "Gears",
  //   "key": "gears",
  //   "schemaType": "long",
  //   "numberConfig": {
  //     "minimum": 1,
  //     "maximum": 24
  //   },
  //   "filterConfig": {
  //     "indexForSearch": true,
  //     "group": "primary",
  //     "label": "Gears"
  //   }
  // }
  // {
  //   key: 'bikeType',
  //   scope: 'public',
  //   schemaType: 'enum',
  //   enumOptions: [
  //     { option: 'city-bikes', label: 'City bikes' },
  //     { option: 'electric-bikes', label: 'Electric bikes' },
  //     { option: 'mountain-bikes', label: 'Mountain bikes' },
  //     { option: 'childrens-bikes', label: "Children's bikes" },
  //   ],
  //   categoryConfig: {
  //     limitToCategoryIds: true,
  //     categoryIds: ['cats'],
  //   },
  //   filterConfig: {
  //     indexForSearch: true,
  //     filterType: 'SelectMultipleFilter', //'SelectSingleFilter',
  //     label: 'Bike type',
  //     group: 'primary',
  //   },
  //   showConfig: {
  //     label: 'Bike type',
  //     isDetail: true,
  //   },
  //   saveConfig: {
  //     label: 'Bike type',
  //     placeholderMessage: 'Select an option…',
  //     isRequired: true,
  //     requiredMessage: 'You need to select a bike type.',
  //   },
  // },
  // {
  //   key: 'tire',
  //   scope: 'public',
  //   schemaType: 'enum',
  //   enumOptions: [
  //     { option: '29', label: '29' },
  //     { option: '28', label: '28' },
  //     { option: '27', label: '27' },
  //     { option: '26', label: '26' },
  //     { option: '24', label: '24' },
  //     { option: '20', label: '20' },
  //     { option: '18', label: '18' },
  //   ],
  //   filterConfig: {
  //     indexForSearch: true,
  //     label: 'Tire size',
  //     group: 'secondary',
  //   },
  //   showConfig: {
  //     label: 'Tire size',
  //     isDetail: true,
  //   },
  //   saveConfig: {
  //     label: 'Tire size',
  //     placeholderMessage: 'Select an option…',
  //     isRequired: true,
  //     requiredMessage: 'You need to select a tire size.',
  //   },
  // },
  // {
  //   key: 'brand',
  //   scope: 'public',
  //   schemaType: 'enum',
  //   enumOptions: [
  //     { option: 'cube', label: 'Cube' },
  //     { option: 'diamant', label: 'Diamant' },
  //     { option: 'ghost', label: 'GHOST' },
  //     { option: 'giant', label: 'Giant' },
  //     { option: 'kalkhoff', label: 'Kalkhoff' },
  //     { option: 'kona', label: 'Kona' },
  //     { option: 'otler', label: 'Otler' },
  //     { option: 'vermont', label: 'Vermont' },
  //   ],
  //   filterConfig: {
  //     indexForSearch: true,
  //     label: 'Brand',
  //     group: 'secondary',
  //   },
  //   showConfig: {
  //     label: 'Brand',
  //     isDetail: true,
  //   },
  //   saveConfig: {
  //     label: 'Brand',
  //     placeholderMessage: 'Select an option…',
  //     isRequired: true,
  //     requiredMessage: 'You need to select a brand.',
  //   },
  // },
  // {
  //   key: 'accessories',
  //   scope: 'public',
  //   schemaType: 'multi-enum',
  //   enumOptions: [
  //     { option: 'bell', label: 'Bell' },
  //     { option: 'lights', label: 'Lights' },
  //     { option: 'lock', label: 'Lock' },
  //     { option: 'mudguard', label: 'Mudguard' },
  //   ],
  //   filterConfig: {
  //     indexForSearch: true,
  //     label: 'Accessories',
  //     searchMode: 'has_all',
  //     group: 'secondary',
  //   },
  //   showConfig: {
  //     label: 'Accessories',
  //   },
  //   saveConfig: {
  //     label: 'Accessories',
  //     placeholderMessage: 'Select an option…',
  //     isRequired: false,
  //   },
  // },
  // // An example of how to use transaction type specific custom fields and private data.
  // {
  //   key: 'note',
  //   scope: 'public',
  //   schemaType: 'text',
  //   listingTypeConfig: {
  //     limitToListingTypeIds: true,
  //     listingTypeIds: ['product-selling'],
  //   },
  //   showConfig: {
  //     label: 'Extra notes',
  //   },
  //   saveConfig: {
  //     label: 'Extra notes',
  //     placeholderMessage: 'Some public extra note about this bike...',
  //   },
  // },
  // {
  //   key: 'privatenote',
  //   scope: 'private',
  //   schemaType: 'text',
  //   listingTypeConfig: {
  //     limitToListingTypeIds: true,
  //     listingTypeIds: ['daily-booking'],
  //   },
  //   saveConfig: {
  //     label: 'Private notes',
  //     placeholderMessage: 'Some private note about this bike...',
  //   },
  // },
];

///////////////////////////////////////////////////////////////////////
// Configurations related to listing types and transaction processes //
///////////////////////////////////////////////////////////////////////

// A presets of supported listing configurations
//
// Note 1: The listingTypes come from listingTypes asset nowadays by default.
//         To use this built-in configuration, you need to change the overwrite from configHelper.js
//         (E.g. use mergeDefaultTypesAndFieldsForDebugging func)
// Note 2: transaction type is part of listing type. It defines what transaction process and units
//         are used when transaction is created against a specific listing.

/**
 * Configuration options for listing experience:
 * - listingType:         Unique string. This will be saved to listing's public data on
 *                        EditListingWizard.
 * - label                Label for the listing type. Used as microcopy for options to select
 *                        listing type in EditListingWizard.
 * - transactionType      Set of configurations how this listing type will behave when transaction is
 *                        created.
 *   - process              Transaction process.
 *                          The process must match one of the processes that this client app can handle
 *                          (check src/util/transactions/transaction.js) and the process must also exists in correct
 *                          marketplace environment.
 *   - alias                Valid alias for the aforementioned process. This will be saved to listing's
 *                          public data as transctionProcessAlias and transaction is initiated with this.
 *   - unitType             Unit type is mainly used as pricing unit. This will be saved to
 *                          transaction's protected data.
 *                          Recommendation: don't use same unit types in completely different processes
 *                          ('item' sold should not be priced the same as 'item' booked).
 * - stockType            This is relevant only to listings using default-purchase process.
 *                        If set to 'oneItem', stock management is not showed and the listing is
 *                        considered unique (stock = 1).
 *                        Possible values: 'oneItem' and 'multipleItems'.
 *                        Default: 'multipleItems'.
 * - defaultListingFields These are tied to transaction processes. Different processes have different flags.
 *                        E.g. default-inquiry can toggle price and location to true/false value to indicate,
 *                        whether price (or location) tab should be shown. If defaultListingFields.price is not
 *                        explicitly set to _false_, price will be shown.
 *                        If the location or pickup is not used, listing won't be returned with location search.
 *                        Use keyword search as main search type if location is not enforced.
 *                        The payoutDetails flag allows provider to bypass setting of payout details.
 *                        Note: customers can't order listings, if provider has not set payout details! Monitor
 *                        providers who have not set payout details and contact them to ensure that they add the details.
 */

/**
 * LOCAL listing types.
 *
 * `mergeListingConfig` (util/configHelpers.js) has a local override,
 * `shouldMerge = true`, so everything here is UNIONED into the hosted Console
 * asset in production — it is NOT dev-only fallback. Anything added here is
 * offered to real users.
 *
 * The hosted asset (/listings/listing-types.json) is the real source of truth
 * and defines exactly two types:
 *
 *   ilmoitus  "Ilmoitus"  -- a posting; companies only
 *   request   "Request"   -- a service request (tarjouspyyntö); private people only
 *
 * `company-profile` is the one entry that must stay local: it is the verified
 * directory entry, created only by server/api/create-company-profile.js or the
 * claim flow, and it is deliberately absent from the hosted asset so it can
 * never be picked in the wizard. It has to remain in the merged config anyway,
 * because EditListingDetailsPanel requires an existing listing's type to be
 * present before it will render the edit form — dropping it here would lock
 * ~45k claimed company owners out of editing their own profile.
 *
 * 'daily-booking' and 'service-request' used to live here. Neither exists in
 * Console, so they were phantom options: they appeared in the wizard with
 * English labels, and every gate written against those ids (e.g. the private-user
 * filter in EditListingDetailsPanel) silently matched nothing. Removed 20.9.2026.
 */
export const listingTypes = [
  {
    listingType: 'company-profile',
    label: 'Company Profile',
    transactionType: {
      process: 'default-inquiry',
      alias: 'default-inquiry/release-1',
      unitType: 'inquiry',
    },
    defaultListingFields: {
      price: false,
      location: true,
      payoutDetails: false, // Companies don't need payout details (they receive inquiries, not payments)
    },
  },
  // // Here are some examples for other listingTypes
  // // TODO: SearchPage does not work well if both booking and product selling are used at the same time
  // {
  //   listingType: 'nightly-booking',
  //   label: 'Nightly booking',
  //   transactionType: {
  //     process: 'default-booking',
  //     alias: 'default-booking/release-1',
  //     unitType: 'night',
  //   },
  // },
  // {
  //   listingType: 'hourly-booking',
  //   label: 'Hourly booking',
  //   transactionType: {
  //     process: 'default-booking',
  //     alias: 'default-booking/release-1',
  //     unitType: 'hour',
  //   },
  // },
  // {
  //   listingType: 'product-selling',
  //   label: 'Sell bicycles',
  //   transactionType: {
  //     process: 'default-purchase',
  //     alias: 'default-purchase/release-1',
  //     unitType: 'item',
  //   },
  //   stockType: 'multipleItems',
  //   defaultListingFields: {
  //     shipping: true,
  //     pickup: true,
  //     payoutDetails: true,
  //   },
  // },
  // {
  //   listingType: 'inquiry',
  //   label: 'Inquiry',
  //   transactionType: {
  //     process: 'default-inquiry',
  //     alias: 'default-inquiry/release-1',
  //     unitType: 'inquiry',
  //   },
  //   defaultListingFields: {
  //     price: false,
  //     location: true,
  //   },
  // },
];

// SearchPage can enforce listing query to only those listings with valid listingType
// However, it only works if you have set 'enum' type search schema for the public data fields
//   - listingType
//
//  Similar setup could be expanded to 2 other extended data fields:
//   - transactionProcessAlias
//   - unitType
//
// Read More:
// https://www.sharetribe.com/docs/how-to/manage-search-schemas-with-flex-cli/#adding-listing-search-schemas
export const enforceValidListingType = false;
